<template>
    <v-container max-width="100%" style="margin-top: 10px">
        <v-chip-group
            v-if="visoes.length > 1"
            :value="visao"
            mandatory
            active-class="primary--text"
            class="tickets-page-views mb-1"
            @change="onVisaoChange"
        >
            <v-chip v-for="key in visoes" :key="key" :value="key" filter outlined>
                <v-icon left small>{{ viewIcons[key] }}</v-icon>
                {{ $t(`ticket.views.${key}`) }}
            </v-chip>
        </v-chip-group>

        <!-- :key recria a visão ao trocar de universo, para filtros e pré-filtro de equipe começarem do zero. -->
        <tickets-jira-view
            v-if="token && user && capacidades"
            ref="ticketsJiraView"
            :key="visao"
            :title="$tc('ticket.self', 2)"
            :token="token"
            :user="user"
            :data-source="capacidades.dataSource"
            :role="capacidades.role"
            :enable-board-status-drag="capacidades.enableBoardStatusDrag"
            :show-new-ticket="!!criacao"
            :edit-checklist="capacidades.editChecklist"
            :show-close-ticket-button="capacidades.showCloseTicketButton"
            :hide-internal-observation="capacidades.hideInternalObservation"
            :hide-precandidatos-write-actions="capacidades.hidePrecandidatosWriteActions"
            :show-print-button="ticketCapabilities.podeImprimir"
            :prefilter-user-teams="capacidades.prefilterUserTeams"
            :can-edit-schedule="ticketCapabilities.podeEditarAgendamento"
            :pode-ver-pendentes="ticketCapabilities.podeVerPendentes"
            :pode-ver-historico="ticketCapabilities.podeVerHistorico"
            @new-ticket="openNewTicket"
            @open-checklist-dialog="onOpenChecklistDialog"
            @request-close-ticket="onRequestCloseTicket"
        />

        <v-alert v-else-if="token && user" type="info" outlined class="mt-4">
            {{ $t('ticket.views.none') }}
        </v-alert>

        <checklist-fulfillment-dialog
            v-if="capacidades && capacidades.editChecklist"
            v-model="checklistDialog"
            :ticket="currentTicket"
            :token="token"
            :user="user"
            :checklists="checklists"
            @saved="onChecklistSaved"
        />

        <ticket-close-dialog
            v-if="capacidades && capacidades.showCloseTicketButton"
            ref="closeDialog"
            v-model="closeDialog"
            :user="user"
            :show-internal-observation="capacidades.closeWithInternalObservation"
            @closed="reloadBoard"
        />

        <ticket-create-flow
            v-if="token && user && criacao"
            ref="createFlow"
            :user="user"
            :token="token"
            :screen="criacao.screen"
            @created="onTicketCreated"
        />
    </v-container>
</template>

<script>
import apiController from "@/utils/apiController";
import EventBus from "../../utils/eventBus";
import { itemIsAssignedToUser } from "@/utils/ticketAssignees";
import { getTicketCapabilities, TICKET_VIEW_ICONS } from "@/utils/ticketPermissions";
import TicketsJiraView from "./TicketsJiraView.vue";
import ChecklistFulfillmentDialog from "./ChecklistFulfillmentDialog.vue";
import TicketCloseDialog from "./TicketCloseDialog.vue";
import TicketCreateFlow from "./TicketCreateFlow.vue";

const VISAO_STORAGE_KEY = 'tickets.visao';

function readJson(key) {
    try {
        return JSON.parse(sessionStorage.getItem(key));
    } catch (e) {
        return null;
    }
}

function readStoredVisao() {
    try {
        return localStorage.getItem(VISAO_STORAGE_KEY);
    } catch (e) {
        return null;
    }
}

function storeVisao(visao) {
    try {
        localStorage.setItem(VISAO_STORAGE_KEY, visao);
    } catch (e) {
        // Preferência de visão é só conveniência.
    }
}

/**
 * Tela única de Tickets: as visões (Atribuídos a mim, Minhas equipes, Supervisão, Abertos por mim)
 * e as funcionalidades de cada uma vêm das permissões de tela do usuário (src/utils/ticketPermissions.js).
 */
export default {
    name: 'TicketsPage',
    components: {
        TicketsJiraView,
        ChecklistFulfillmentDialog,
        TicketCloseDialog,
        TicketCreateFlow,
    },
    data: () => ({
        user: null,
        token: null,
        permissoes: [],
        visao: null,
        viewIcons: TICKET_VIEW_ICONS,
        checklists: [],
        checklistDialog: false,
        currentTicket: null,
        closeDialog: false,
    }),
    computed: {
        ticketCapabilities() {
            return getTicketCapabilities(this.user, this.permissoes);
        },
        visoes() {
            return this.ticketCapabilities.visoes;
        },
        capacidades() {
            return (this.visao && this.ticketCapabilities.porVisao[this.visao]) || null;
        },
        /** Criação é do usuário: o botão aparece em qualquer visão que ele abrir. */
        criacao() {
            return this.ticketCapabilities.criacao;
        },
    },
    watch: {
        '$route.query.visao'() {
            this.resolveVisao();
        },
        capacidades: {
            immediate: true,
            handler(caps) {
                if (caps && caps.editChecklist && this.checklists.length === 0) {
                    this.PopulaChecklists();
                }
            },
        },
    },
    created() {
        this.user = readJson('user');
        this.token = sessionStorage.getItem('api');
        this.permissoes = readJson('permissoes') || [];
        this.resolveVisao();
    },
    methods: {
        /** Visão da URL; senão a última usada; senão a primeira permitida. */
        resolveVisao() {
            const candidatas = [this.$route.query.visao, readStoredVisao()];
            const visao = candidatas.find((v) => v && this.visoes.includes(v)) || this.visoes[0] || null;
            this.visao = visao;
            if (visao && this.$route.query.visao !== visao) {
                this.$router.replace({ query: { ...this.$route.query, visao } }).catch(() => {});
            }
        },
        onVisaoChange(visao) {
            if (!visao || visao === this.visao) return;
            storeVisao(visao);
            this.$router.replace({ query: { ...this.$route.query, visao } }).catch(() => {});
        },
        reloadBoard() {
            const view = this.$refs.ticketsJiraView;
            if (view && typeof view.loadData === 'function') view.loadData();
        },
        PopulaChecklists() {
            apiController.request(apiController.endpoints.getallChecklistsativos, {
                onSuccess: (response) => {
                    this.checklists = response.data;
                },
            });
        },
        openNewTicket() {
            if (this.$refs.createFlow) this.$refs.createFlow.open();
        },
        /** Ticket criado: só atualiza o quadro; o card não fica aberto. */
        onTicketCreated() {
            this.reloadBoard();
        },
        onRequestCloseTicket(payload) {
            if (this.$refs.closeDialog) this.$refs.closeDialog.open(payload);
        },
        /** Sem permissão de despachante, só quem está atribuído ou é despachante de uma equipe do ticket preenche o checklist. */
        async canFillChecklist(item) {
            if (!this.capacidades.checklistRequiresAssignment) return true;
            const uid = this.user && this.user.id_usuario;
            if (!item || uid == null) return false;
            if (itemIsAssignedToUser(item, uid)) return true;
            try {
                const response = await apiController.request(apiController.endpoints.getTicketequipes, {
                    params: { id: item.id_ticket },
                    silent: true,
                });
                const teamIds = Array.isArray(response.data) ? response.data : [];
                const results = await Promise.all(teamIds.map((teamId) => apiController.request(apiController.endpoints.getUsuariosporequipe, {
                    params: { id: teamId },
                    silent: true,
                })
                    .then((res) => (res.data || []).some((u) => Number(u.id_usuario) === Number(uid)
                        && (Number(u.id_tipo_usuario) === 2 || u.is_dispatcher === 'Sim')))
                    .catch(() => false)));
                return results.some(Boolean);
            } catch (e) {
                return false;
            }
        },
        async onOpenChecklistDialog(item) {
            if (!(await this.canFillChecklist(item))) {
                EventBus.$emit('show-alert', {
                    tipo: 'error',
                    mensagem: this.$t('ticket.checklist.error.onlyAssignedUserCanFulfill')
                });
                return;
            }
            this.currentTicket = item;
            this.checklistDialog = true;
        },
        onChecklistSaved() {
            const view = this.$refs.ticketsJiraView;
            if (view && typeof view.onChecklistSavedFromParent === 'function') {
                view.onChecklistSavedFromParent(this.currentTicket);
            }
            this.reloadBoard();
        },
    },
};
</script>

<style scoped>
.tickets-page-views {
    padding: 0 4px;
}
</style>
